// Worksheet rendering.
//
// Composites the user's answers onto the original worksheet photo using a canvas. The
// output is what gets exported to PDF and PNG and what the placement editor shows
// live, so the preview and the export always match. The original photo is never
// modified - we draw on top of it each time.
//
// Where each answer goes, in priority order:
//   1. A placement the pupil set by dragging / resizing in the editor (always wins).
//   2. The AI's answerBox, nudged onto a printed answer line if one sits right there
//      (autoPlaceAnswers). Since the October 2026 model upgrade the AI boxes are
//      accurate, so the nudge is deliberately local: it never moves a box to a
//      different row, it only settles text onto the line it is already on.
//
// Font: Lexend Deca (designed for reading proficiency), matching the app.

import type { WorksheetQuestion, QuestionBox } from './vision-types';

/** Normalised (0-1) answer boxes keyed by question id. */
export type AnswerPlacements = Record<string, QuestionBox>;

interface RenderOptions {
  /** Hex colour for rendered answers. Default: deep indigo on paper, white on dark areas (e.g. coloured headers). */
  answerColor?: string;
  /** If true, draw a subtle outline of each answer box. Useful for debugging. */
  debugBoxes?: boolean;
}

const FONT_FAMILY = '"Lexend Deca", system-ui, sans-serif';
const DEFAULT_COLOR = '#1f2960';
const ON_DARK_COLOR = '#ffffff';

export function loadImage(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/**
 * Render the worksheet photo with answers overlaid onto a new canvas. Used for the
 * PDF / PNG export and the saved library copy.
 */
export async function renderWorksheet(
  imageDataUrl: string,
  questions: WorksheetQuestion[],
  answers: Record<string, string>,
  placements: AnswerPlacements = {},
  options: RenderOptions = {},
): Promise<HTMLCanvasElement> {
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D context unavailable.');
  const auto = autoPlaceAnswers(img, questions);
  drawWorksheet(ctx, img, questions, answers, { ...auto, ...placements }, options, placements);
  return canvas;
}

/**
 * Draw the photo plus every non-empty answer into ctx (sized to the image).
 * `boxes` holds the final box for every question; `userPlaced` marks the ones the
 * pupil sized themselves, which lifts the automatic font cap so "make it bigger"
 * actually makes the text bigger.
 */
export function drawWorksheet(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  questions: WorksheetQuestion[],
  answers: Record<string, string>,
  boxes: AnswerPlacements,
  options: RenderOptions = {},
  userPlaced: AnswerPlacements = {},
): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  ctx.clearRect(0, 0, W, H);
  ctx.drawImage(img, 0, 0, W, H);
  const padding = Math.max(3, Math.round(H * 0.003));

  for (const q of questions) {
    const text = (answers[q.id] ?? '').trim();
    if (!text) continue;
    const box = denormalise(boxes[q.id] ?? q.answerBox, W, H);
    renderAnswerInBox(ctx, text, box, {
      color: options.answerColor ?? (isDarkArea(ctx, box) ? ON_DARK_COLOR : DEFAULT_COLOR),
      padding,
      multiLine: q.answerType === 'multi_line' || q.expectedFormat === 'paragraph',
      centred: q.answerType === 'box',
      imageHeight: H,
      userSized: !!userPlaced[q.id],
    });
    if (options.debugBoxes) {
      ctx.save();
      ctx.strokeStyle = 'rgba(3, 105, 161, 0.6)';
      ctx.lineWidth = 2;
      ctx.strokeRect(box.x, box.y, box.w, box.h);
      ctx.restore();
    }
  }
}

/** True if the photo under this box is dark (average luma below ~45%), sampled sparsely. */
function isDarkArea(ctx: CanvasRenderingContext2D, box: { x: number; y: number; w: number; h: number }): boolean {
  const x = Math.max(0, box.x);
  const y = Math.max(0, box.y);
  const w = Math.min(ctx.canvas.width - x, box.w);
  const h = Math.min(ctx.canvas.height - y, box.h);
  if (w < 1 || h < 1) return false;
  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(x, y, w, h).data;
  } catch {
    return false;
  }
  const step = Math.max(1, Math.floor(data.length / 4 / 400)) * 4;
  let sum = 0;
  let n = 0;
  for (let i = 0; i < data.length; i += step) {
    sum += (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
    n++;
  }
  return n > 0 && sum / n < 115;
}

// ---------------------------------------------------------------------------
// Automatic placement: AI box + local line nudge
// ---------------------------------------------------------------------------

type Underline = { y: number; xs: number; xe: number };

/**
 * Starting box for every question (normalised). For line answers, if a printed
 * underline runs under the AI's box (within a small vertical window and covering
 * most of its width), the box is settled onto that line so the text sits where a
 * child would write. Everything else keeps the AI's box unchanged.
 */
export function autoPlaceAnswers(img: HTMLImageElement, questions: WorksheetQuestion[]): AnswerPlacements {
  const out: AnswerPlacements = {};
  for (const q of questions) out[q.id] = { ...q.answerBox };

  const lineQs = questions.filter((q) => q.answerType === 'line');
  if (!lineQs.length) return out;

  const W = img.naturalWidth;
  const H = img.naturalHeight;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return out;
  ctx.drawImage(img, 0, 0);
  let pixels: ImageData;
  try {
    pixels = ctx.getImageData(0, 0, W, H);
  } catch {
    return out; // tainted canvas - keep the AI boxes
  }
  const lines = detectUnderlines(toGrayscale(pixels), W, H);

  for (const q of lineQs) {
    const b = denormalise(q.answerBox, W, H);
    const windowTop = b.y + b.h * 0.3;
    const windowBottom = b.y + b.h + H * 0.012;
    let best: Underline | null = null;
    let bestDist = Infinity;
    for (const ln of lines) {
      if (ln.y < windowTop || ln.y > windowBottom) continue;
      const overlap = Math.max(0, Math.min(b.x + b.w, ln.xe) - Math.max(b.x, ln.xs));
      if (overlap < b.w * 0.6) continue;
      if (ln.xe - ln.xs > b.w * 1.8) continue; // a table border or rule, not this answer line
      const dist = Math.abs(ln.y - (b.y + b.h));
      if (dist < bestDist) {
        best = ln;
        bestDist = dist;
      }
    }
    if (!best) continue;
    const h = Math.max(b.h, H * 0.022);
    const x = Math.max(b.x, best.xs + 2);
    const xe = Math.min(b.x + b.w, best.xe - 2);
    out[q.id] = { x: x / W, y: (best.y - h * 0.85) / H, w: Math.max(xe - x, b.w * 0.5) / W, h: h / H };
  }
  return out;
}

function toGrayscale(imgData: ImageData): Uint8ClampedArray {
  const { data, width, height } = imgData;
  const out = new Uint8ClampedArray(width * height);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    out[j] = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
  }
  return out;
}

/** Thin horizontal dark runs at least 3% of the page wide, merged across adjacent rows. */
function detectUnderlines(gray: Uint8ClampedArray, w: number, h: number): Underline[] {
  const minRun = Math.max(20, Math.floor(w * 0.03));
  const raw: Underline[] = [];
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let runStart = -1;
    for (let x = 0; x <= w; x++) {
      const dark = x < w && gray[row + x] < 130;
      if (dark) {
        if (runStart === -1) runStart = x;
        continue;
      }
      if (runStart === -1) continue;
      if (x - runStart >= minRun) {
        let aboveWhite = 0;
        let belowWhite = 0;
        for (let ax = runStart; ax < x; ax++) {
          for (let dy = 1; dy <= 2; dy++) {
            if (y - dy >= 0 && gray[(y - dy) * w + ax] > 180) aboveWhite++;
            if (y + dy < h && gray[(y + dy) * w + ax] > 180) belowWhite++;
          }
        }
        const samples = (x - runStart) * 2;
        if (aboveWhite / samples > 0.5 || belowWhite / samples > 0.5) raw.push({ y, xs: runStart, xe: x });
      }
      runStart = -1;
    }
  }
  raw.sort((a, b) => a.y - b.y || a.xs - b.xs);
  const merged: Array<{ yMin: number; yMax: number; xs: number; xe: number }> = [];
  for (const ln of raw) {
    const c = merged.find(
      (m) => Math.abs(ln.y - m.yMax) <= 3 && Math.abs(ln.xs - m.xs) < 10 && Math.abs(ln.xe - m.xe) < 10,
    );
    if (c) {
      c.yMax = Math.max(c.yMax, ln.y);
      c.xs = Math.min(c.xs, ln.xs);
      c.xe = Math.max(c.xe, ln.xe);
    } else {
      merged.push({ yMin: ln.y, yMax: ln.y, xs: ln.xs, xe: ln.xe });
    }
  }
  return merged.map((c) => ({ y: Math.floor((c.yMin + c.yMax) / 2), xs: c.xs, xe: c.xe }));
}

// ---------------------------------------------------------------------------
// Highlight for the answer screen
// ---------------------------------------------------------------------------

/** Outline one answer box on the photo (the live answer screen's "you are here"). */
export function drawQuestionHighlight(
  ctx: CanvasRenderingContext2D,
  imageWidth: number,
  imageHeight: number,
  box: QuestionBox,
  options: { color?: string; thickness?: number } = {},
) {
  const px = denormalise(box, imageWidth, imageHeight);
  ctx.save();
  ctx.strokeStyle = options.color ?? '#0369a1';
  ctx.lineWidth = options.thickness ?? Math.max(2, Math.round(imageWidth * 0.004));
  ctx.lineJoin = 'round';
  ctx.shadowColor = 'rgba(3, 105, 161, 0.35)';
  ctx.shadowBlur = 12;
  ctx.strokeRect(px.x, px.y, px.w, px.h);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Text layout
// ---------------------------------------------------------------------------

function denormalise(box: QuestionBox, w: number, h: number) {
  return {
    x: Math.round(box.x * w),
    y: Math.round(box.y * h),
    w: Math.round(box.w * w),
    h: Math.round(box.h * h),
  };
}

interface AnswerRenderOpts {
  color: string;
  padding: number;
  multiLine: boolean;
  /** Centre single-line text horizontally (printed boxes: a letter or number sits in the middle). */
  centred: boolean;
  imageHeight: number;
  /** The pupil sized this box: follow the box, ignore the automatic page-size cap. */
  userSized: boolean;
}

function deriveFontSize(opts: AnswerRenderOpts, boxHeight: number): number {
  // The answer box is the truth: 72% of its height for single lines (close to the
  // printed text size on a typical answer line, which matters for dyslexic readers),
  // 35% for multi-line so several lines fit. Automatic boxes are also
  // capped at 1.8% of the page height (~32px on A4) so a large AI box never gives
  // shouty text. A box the pupil resized follows the box up to 8% of the page.
  const verticalRoom = Math.max(10, boxHeight - opts.padding * 2);
  const fromBox = opts.multiLine ? verticalRoom * 0.35 : verticalRoom * 0.72;
  const cap = opts.imageHeight * (opts.userSized ? 0.08 : 0.018);
  return Math.max(12, Math.round(Math.min(fromBox, cap)));
}

function setFont(ctx: CanvasRenderingContext2D, size: number) {
  ctx.font = `500 ${size}px ${FONT_FAMILY}`;
}

function renderAnswerInBox(
  ctx: CanvasRenderingContext2D,
  text: string,
  box: { x: number; y: number; w: number; h: number },
  opts: AnswerRenderOpts,
) {
  ctx.save();
  ctx.fillStyle = opts.color;
  ctx.textAlign = 'left';
  const availW = Math.max(10, box.w - opts.padding * 2);
  let size = deriveFontSize(opts, box.h);

  if (!opts.multiLine) {
    // Single line: shrink until it fits the width, centred vertically in the box.
    setFont(ctx, size);
    while (size > 10 && ctx.measureText(text).width > availW) {
      size -= 1;
      setFont(ctx, size);
    }
    ctx.textBaseline = 'middle';
    if (opts.centred) {
      ctx.textAlign = 'center';
      ctx.fillText(text, box.x + box.w / 2, box.y + box.h / 2);
    } else {
      ctx.fillText(text, box.x + opts.padding, box.y + box.h / 2);
    }
  } else {
    // Multi-line: wrap to the width, shrink until it fits the height.
    const availH = box.h - opts.padding * 2;
    let lines = wrapText(ctx, text, availW, size);
    while (size > 10 && lines.length * size * 1.15 > availH) {
      size -= 1;
      lines = wrapText(ctx, text, availW, size);
    }
    ctx.textBaseline = 'top';
    const lineHeight = size * 1.15;
    const startY = box.y + opts.padding + Math.max(0, (availH - lines.length * lineHeight) / 2);
    lines.forEach((line, i) => ctx.fillText(line, box.x + opts.padding, startY + i * lineHeight));
  }
  ctx.restore();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, size: number): string[] {
  setFont(ctx, size);
  const wrapped: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    const words = line.split(/\s+/).filter(Boolean);
    if (!words.length) {
      wrapped.push('');
      continue;
    }
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) {
        current = candidate;
      } else {
        if (current) wrapped.push(current);
        current = word;
      }
    }
    if (current) wrapped.push(current);
  }
  return wrapped;
}
